import { createStyles } from '@mantine/core';

import {
  SUB_HEADER_HEIGHT,
  SUB_HEADER_HEIGHT_WITH_WIDGET,
  SUB_HEADER_STICKY_POSITION,
} from '../../constants';

import { getCommonStyles, type SubNavLinkStyles } from './SubHeader.common.styles';
import type { SubNavVariant } from './types';

const useStyles = createStyles(
  (
    theme,
    {
      isSticky,
      hasWidget,
      hasSubMenuItems,
      subNavVariant,
    }: {
      isSticky?: boolean;
      hasWidget?: boolean;
      hasSubMenuItems?: boolean;
      subNavVariant?: SubNavVariant;
    }
  ) => {
    const common = getCommonStyles(theme);
    const baseLink = common.subNavLink;
    const subNavLink: SubNavLinkStyles =
      subNavVariant === 'underline'
        ? {
            ...baseLink,
            borderTop: '1px solid transparent',
            borderBottom: '1px solid transparent',
            '&.active': {
              ...baseLink['&.active'],
              fontWeight: 600,
            },
          }
        : baseLink;

    return {
      subHeaderContainer: {
        position: isSticky ? 'sticky' : 'relative',
        top: SUB_HEADER_STICKY_POSITION,
        height: hasSubMenuItems ? SUB_HEADER_HEIGHT : 80,
        zIndex: theme.other.zIndex.subHeader,

        [theme.fn.smallerThan('sm')]: {
          top: isSticky && hasWidget ? SUB_HEADER_STICKY_POSITION - 64 : SUB_HEADER_STICKY_POSITION,
          height: (() => {
            if (!hasSubMenuItems) return 80;
            return hasWidget ? SUB_HEADER_HEIGHT_WITH_WIDGET : SUB_HEADER_HEIGHT;
          })(),
        },
      },

      subHeaderContent: {
        position: 'relative',
        zIndex: 1,
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: hasWidget ? '1fr 1fr' : '1fr',
        gridTemplateRows: hasSubMenuItems ? 'auto 50px' : '1fr',
        alignItems: hasSubMenuItems ? 'end' : 'center',
        padding: `0 ${theme.other.spacing._24}px`,
        width: '100%',
        height: '100%',
        maxWidth: theme.other.layout.maxWidth,
        color: theme.white,

        [theme.fn.smallerThan('sm')]: {
          padding: `0 ${theme.other.spacing._16}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: hasSubMenuItems ? 'flex-end' : 'center',
          gap: theme.other.spacing._8,
        },
      },

      titleRow: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        justifyContent: 'space-between',
        gap: theme.other.spacing._12,
        marginBottom: hasSubMenuItems ? theme.other.spacing._8 : 0,

        '& > h1': {
          marginBottom: 0,
        },
      },

      subHeaderTitle: {
        marginTop: 0,
        marginBottom: hasSubMenuItems ? theme.other.spacing._8 : 0,
        color: theme.white,
        fontSize: 28,
        fontStyle: 'normal',
        lineHeight: '32px',
        fontWeight: 700,
        letterSpacing: '-0.02em',

        [theme.fn.smallerThan('sm')]: {
          fontSize: 24,
        },
      },

      subHeaderWidget: {
        color: theme.white,
        fontSize: 28,
        gridColumn: '2 / 2',
        gridRow: '1 / span 2',
        alignSelf: 'center',
        justifySelf: 'end',

        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.other.spacing._48,

        [theme.fn.smallerThan('md')]: {
          gap: theme.other.spacing._24,
        },

        [theme.fn.smallerThan('sm')]: {
          borderRadius: /* eslint-disable-line local-rules/use-theme-tokens */ 12,
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          padding: `${theme.other.spacing._8}px ${theme.other.spacing._16}px`,
        },
      },

      widgetColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.other.spacing._4,
        whiteSpace: 'nowrap',
      },

      widgetDivider: {
        width: 1,
        height: 48,
        backgroundColor: 'rgba(255, 255, 255, 0.2)',
        marginLeft: -theme.other.spacing._16,
        marginRight: -theme.other.spacing._24,

        [theme.fn.smallerThan('md')]: {
          marginLeft: -theme.other.spacing._8,
          marginRight: -theme.other.spacing._12,
        },
      },

      widgetActionButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 44,
        height: 44,
        borderRadius: '50%',
        backgroundColor: theme.colors.gray2[6],
        color: theme.white,
        cursor: 'pointer',
        transition: 'background-color 0.2s ease-in-out',
        flexShrink: 0,

        '&:hover': {
          backgroundColor: theme.colors.gray2[5],
        },
      },

      ...common,
      svgBgrBottomAnchored: {
        top: 'auto',
        bottom: 0,
      },
      subNavLink,
    };
  }
);

export default useStyles;
